// Forecast API service - switches between mock and real API

import {
  ForecastProject,
  TurbineForForecast,
  ListForecastsResponse,
  CreateForecastRequest
} from '@/types/forecast';
import { FailureFilters } from '@/types/filters';
import { mockForecastApi } from './mockForecastApi';
import { api } from './client';
import { isConfigEnabled } from '@/config/config';
import {
  transformForecastToBackend,
  transformForecastFromBackend,
  transformForecastUpdateToBackend,
  transformForecastListFromBackend
} from './forecastTransformers';
import { BackendForecast, BackendForecastListResponse } from '@/types/backend';


/**
 * Main forecast API service
 * Switches between mock (localStorage) and real API based on configuration
 */
export const forecastApi = {
  /**
   * Get turbines matching forecast filters (for population calculation)
   */
  async getTurbinesForForecast(filters: FailureFilters): Promise<TurbineForForecast[]> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockForecastApi.getTurbinesForForecast(filters);
    }

    // Real API implementation
    return api.get<TurbineForForecast[]>('/forecast/turbines', { params: filters });
  },

  /**
   * Save forecast workspace
   */
  async saveForecast(workspace: ForecastProject | CreateForecastRequest, userId: string): Promise<ForecastProject> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockForecastApi.saveForecast(workspace as ForecastProject);
    }

    // Real API implementation - use Weibull forecast endpoint
    const backendRequest = transformForecastToBackend(workspace, userId);
    const response = await api.post<BackendForecast>('/forecasts/weibull', backendRequest);
    return transformForecastFromBackend(response);
  },

  /**
   * Load forecast workspace by ID
   */
  async loadForecast(id: string): Promise<ForecastProject> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockForecastApi.loadForecast(id);
    }

    // Real API implementation - use Weibull forecast endpoint
    const response = await api.get<BackendForecast>(`/forecasts/weibull/${id}`);
    return transformForecastFromBackend(response);
  },

  /**
   * Update existing forecast workspace
   */
  async updateForecast(id: string, updates: Partial<ForecastProject>, userId: string): Promise<ForecastProject> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockForecastApi.updateForecast(id, updates);
    }

    // Real API implementation - use Weibull forecast endpoint
    const backendRequest = transformForecastUpdateToBackend(id, updates, userId);
    const response = await api.put<BackendForecast>(`/forecasts/weibull`, backendRequest);
    return transformForecastFromBackend(response);
  },

  /**
   * List forecast workspaces
   */
  async listForecasts(): Promise<ListForecastsResponse> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockForecastApi.listForecasts();
    }

    // Real API implementation - use Weibull forecast endpoint
    const response = await api.get<BackendForecastListResponse>('/forecasts/weibull');
    return transformForecastListFromBackend(response);
  },

  /**
   * Delete forecast workspace
   */
  async deleteForecast(id: string): Promise<void> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      return mockForecastApi.deleteForecast(id);
    }

    // Real API implementation - use Weibull forecast endpoint
    await api.delete(`/forecasts/weibull/${id}`);
  },

  /**
   * Get forecasts by workspace ID
   */
  async getForecastsByWorkspace(workspaceId: string): Promise<ListForecastsResponse> {
    if (isConfigEnabled('REACT_APP_USE_MOCK_API')) {
      // Filter mock forecasts by workspace
      const all = await mockForecastApi.listForecasts();
      const filtered = all.forecasts.filter(f => f.sourceAnalysisWorkspaceId === workspaceId);
      return { forecasts: filtered, total: filtered.length };
    }

    // Real API implementation - use Weibull forecast endpoint
    const response = await api.get<BackendForecastListResponse>(`/forecasts/weibull/workspace/${workspaceId}`);
    return transformForecastListFromBackend(response);
  }
};
